import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackToDashboard from '../components/BackToDashboard';
import { inboxApi } from '../api/client';
import type { InboxEntry } from '../api/client';

const PAGE_SIZE = 20;

type FilterRead = 'all' | 'unread' | 'read';
type SortLeads = 'newest' | 'oldest';

function escapeCsvCell(s: string): string {
  const str = String(s ?? '').replace(/"/g, '""');
  return /[",\n\r]/.test(str) ? `"${str}"` : str;
}

export default function LeadsInbox() {
  const { t } = useTranslation();

  const [items, setItems] = useState<InboxEntry[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [filterRead, setFilterRead] = useState<FilterRead>('all');
  const [sortBy, setSortBy] = useState<SortLeads>('newest');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await inboxApi.list();
      setItems(res.items);
      setUnreadCount(res.unreadCount);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load inbox');
      setItems([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredAndSorted = useMemo(() => {
    let list = [...items];
    if (filterRead === 'unread') list = list.filter((e) => e.readStatus === 'new');
    if (filterRead === 'read') list = list.filter((e) => e.readStatus === 'read');
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (e) =>
          (e.leadName ?? '').toLowerCase().includes(q) ||
          (e.leadEmail ?? '').toLowerCase().includes(q) ||
          (e.leadMessage ?? '').toLowerCase().includes(q) ||
          (e.listingTitleEn ?? '').toLowerCase().includes(q) ||
          (e.leadPhone ?? '').includes(q)
      );
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      list = list.filter((e) => new Date(e.createdAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((e) => new Date(e.createdAt) <= to);
    }
    list.sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return sortBy === 'newest' ? tb - ta : ta - tb;
    });
    return list;
  }, [items, filterRead, sortBy, search, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));
  const effectivePage = Math.min(page, totalPages);
  const paginatedLeads = useMemo(
    () => filteredAndSorted.slice((effectivePage - 1) * PAGE_SIZE, effectivePage * PAGE_SIZE),
    [filteredAndSorted, effectivePage]
  );

  useEffect(() => {
    setPage(1);
  }, [search, dateFrom, dateTo, filterRead, sortBy]);

  const handleMarkRead = async (notificationId: string) => {
    setMarkingId(notificationId);
    try {
      await inboxApi.markRead(notificationId);
      setItems((prev) =>
        prev.map((e) => (e.notificationId === notificationId ? { ...e, readStatus: 'read' as const } : e))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      window.dispatchEvent(new Event('inbox-updated'));
    } catch {
      // keep state as-is
    } finally {
      setMarkingId(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const formatDateForCsv = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toISOString();
    } catch {
      return iso;
    }
  };

  const exportToExcel = () => {
    const headers = ['Timestamp', 'Name', 'Email', 'Phone', 'Message', 'Listing', 'Status', 'Lead ID'];
    const rows = filteredAndSorted.map((e) => [
      formatDateForCsv(e.createdAt),
      e.leadName,
      e.leadEmail,
      e.leadPhone ?? '',
      e.leadMessage.replace(/\r?\n/g, ' '),
      e.listingTitleEn,
      e.readStatus === 'new' ? 'New' : 'Read',
      e.leadId,
    ]);
    const csv = [headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.map(escapeCsvCell).join(','))].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="dash-page dash-page--wide">
        <BackToDashboard />
        <h1 className="dash-page-title">{t('dashboard.leadsInbox')}</h1>
        <p style={{ color: '#6b7280' }}>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="dash-page dash-page--wide">
      <BackToDashboard />
      <header className="dash-page-header">
        <h1 className="dash-page-title">{t('dashboard.leadsInbox')}</h1>
        <p className="dash-page-desc">
          {t('dashboard.inboxTitle')} — {unreadCount} {t('dashboard.inboxNew').toLowerCase()}. {t('dashboard.inboxDescription')}
        </p>
      </header>

      {error && <div className="dash-alert dash-alert--error">{error}</div>}

      {items.length === 0 ? (
        <section className="dash-card">
          <div className="dash-card-body">
            <div className="dash-empty">
              <p style={{ margin: 0 }}>{t('dashboard.inboxEmpty')}</p>
              <Link to="/listings" className="dash-btn dash-btn--primary" style={{ marginTop: '1rem', textDecoration: 'none' }}>Browse listings</Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="dash-card">
          <div className="dash-card-body">
            <div className="dash-inbox-toolbar">
              <input
                type="search"
                placeholder="Search name, email, message, listing…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dash-form-input"
                style={{ minWidth: 200 }}
              />
              <span className="dash-toolbar-label">From</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="dash-form-input" style={{ width: 'auto' }} />
              <span className="dash-toolbar-label">To</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="dash-form-input" style={{ width: 'auto' }} />
              <div className="dash-filter-chips">
                <button type="button" className={`dash-filter-chip ${filterRead === 'all' ? 'active' : ''}`} onClick={() => setFilterRead('all')}>All ({items.length})</button>
                <button type="button" className={`dash-filter-chip ${filterRead === 'unread' ? 'active' : ''}`} onClick={() => setFilterRead('unread')}>{t('dashboard.inboxNew')} ({unreadCount})</button>
                <button type="button" className={`dash-filter-chip ${filterRead === 'read' ? 'active' : ''}`} onClick={() => setFilterRead('read')}>{t('dashboard.inboxRead')} ({items.length - unreadCount})</button>
              </div>
              <span className="dash-toolbar-label">Sort</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortLeads)} className="dash-form-select">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <button type="button" onClick={exportToExcel} className="dash-btn dash-btn--primary" style={{ marginLeft: 'auto' }}>
                Export to Excel
              </button>
            </div>

            <p className="dash-pagination-info" style={{ marginBottom: '0.5rem', textAlign: 'left', fontSize: '0.8125rem', color: '#6b7280' }}>
              Showing {filteredAndSorted.length === 0 ? 0 : (effectivePage - 1) * PAGE_SIZE + 1}–{Math.min(effectivePage * PAGE_SIZE, filteredAndSorted.length)} of {filteredAndSorted.length} leads (max {PAGE_SIZE} per page)
            </p>

            <div className="dash-table-wrap dash-inbox-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Date & time</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Message</th>
                    <th>Listing</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLeads.map((entry) => (
                    <tr key={entry.notificationId} className={entry.readStatus === 'new' ? 'dash-lead-row-unread' : ''}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.8125rem', color: '#374151' }} title={entry.createdAt}>
                        {formatDate(entry.createdAt)}
                      </td>
                      <td>{entry.leadName}</td>
                      <td>{entry.leadEmail}</td>
                      <td>{entry.leadPhone || '—'}</td>
                      <td title={entry.leadMessage}>
                        {entry.leadMessage.length > 80 ? entry.leadMessage.slice(0, 80) + '…' : entry.leadMessage}
                      </td>
                      <td style={{ fontSize: '0.8125rem' }}>{entry.listingTitleEn}</td>
                      <td>
                        <span className={`dash-badge ${entry.readStatus === 'new' ? 'dash-badge--info' : 'dash-badge--neutral'}`} style={{ fontSize: '0.7rem' }}>
                          {entry.readStatus === 'new' ? t('dashboard.inboxNew') : t('dashboard.inboxRead')}
                        </span>
                      </td>
                      <td>
                        <div className="dash-actions-inline">
                          {entry.readStatus === 'new' && (
                            <button type="button" disabled={markingId === entry.notificationId} onClick={() => handleMarkRead(entry.notificationId)} className="dash-btn dash-btn--secondary dash-btn--sm">
                              {markingId === entry.notificationId ? '…' : t('dashboard.markAsRead')}
                            </button>
                          )}
                          <Link to={`/dashboard/leads/entry/${entry.notificationId}`} className="dash-btn dash-btn--primary dash-btn--sm" style={{ textDecoration: 'none' }}>{t('dashboard.viewDetails')}</Link>
                          <Link to={`/listings/${entry.listingId}`} className="dash-btn dash-btn--secondary dash-btn--sm" style={{ textDecoration: 'none' }}>{t('dashboard.viewListing')}</Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="dash-pagination" style={{ marginTop: '1rem' }}>
                <button type="button" disabled={effectivePage <= 1} onClick={() => setPage((p) => p - 1)} className="dash-btn dash-btn--secondary dash-btn--sm">Previous</button>
                <span className="dash-pagination-info">Page {effectivePage} of {totalPages}</span>
                <button type="button" disabled={effectivePage >= totalPages} onClick={() => setPage((p) => p + 1)} className="dash-btn dash-btn--secondary dash-btn--sm">Next</button>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
